import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useEffect, useMemo, useState } from "react";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import {
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TablePagination,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import moment from "moment";
import StatusChip from "src/shared/components/status-chip";
import { notificationsState } from "src/store/notifications";
import { useRecoilState } from "recoil";
import { LoadingButton } from "@mui/lab";
import { downloadFile } from "src/utils";
import { useGetLocationsList } from "src/api/locations";
import {
  useExportTransactionsList,
  useGetTransactionsList,
} from "src/api/transactions";
import NoFilterSelected from "src/shared/components/no-filters-selected";
import TransactionsDetailsDrawer from "../../components/transaction-details-popup";
import { paymentsTypes } from "../../constants";
import { ICity } from "src/types/generic";
import { useGetCitiesList } from "src/api/general";
import { DatePicker } from "@mui/x-date-pickers";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";
import { serviceLabels } from "src/constants/general";

const allOption = "All";
const minDate = new Date("2024-01-01T00:00:00");
const maxDate = moment().add(1, "year").endOf("year").toDate();

const tableHeadText = [
  "Ticket Num",
  "Date",
  "Location Name",
  "Service",
  "Service type",
  "Check-out",
  "Payment Status",
  "Status",
];

const TransactionsTable = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [location, setLocation] = useState<any>(null);
  const [selectedCity, setSelectedCity] = useState<ICity>();
  const [searchBy, setSearchBy] = useState<string>("");
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedPaymentType, setSelectedPaymentType] = useState<string>("all");
  const [selectedService, setSelectedService] = useState<string>("all");
  const [open, setOpen] = useState(false);
  const [clickedRow, setClickedRow] = useState();
  const [lastDocumentIds, setLastDocumentIds] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [openStartDate, setOpenStartDate] = useState<boolean>(false);
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [openEndDate, setOpenEndDate] = useState<boolean>(false);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [customerNum, setCustomerNum] = useState<string>("");

  const startingDate = moment(startDate).startOf("day").valueOf();

  const endingDate = moment(endDate).endOf("day").valueOf();

  const allFilters = {
    ...(selectedCity && { cityId: selectedCity.id }),
    ...(location && { location: location?.id }),
    ...(startDate && { from: `${startingDate}` }),
    ...(endDate && { to: `${endingDate}` }),
    ...(searchBy && { search: searchBy }),
    ...(customerNum && { customerNumber: customerNum }),
    type: "upcoming",
    ...(selectedService && { service: `${selectedService}` }),
    ...(selectedPaymentType !== "all" && {
      paymentTypes: [selectedPaymentType],
    }),
  };

  // APIS
  const { data: locationsData } = useGetLocationsList();
  const {
    data: upcomingTransData,
    isLoading,
    error,
    status,
  } = useGetTransactionsList({
    ...allFilters,
    limit: `${rowsPerPage}`,
    ...(!!lastDocumentIds[lastDocumentIds.length - 1] && {
      startAfter: lastDocumentIds[lastDocumentIds.length - 1],
    }),
  });
  const { data: citiesData } = useGetCitiesList({
    type: "active",
  });

  const { refetch: refetchExportUpcoming, isFetching: isFetchingExport } =
    useExportTransactionsList({ ...allFilters });

  const handleChangePage = (event: unknown, newPage: number) => {
    if (newPage > page) {
      setLastDocumentIds((prev) => [
        ...prev,
        upcomingTransData?.data?.[upcomingTransData?.data?.length - 1]?.id,
      ]);
    } else {
      setLastDocumentIds((prev) => prev.slice(0, newPage));
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLastDocumentIds([]);
  };

  const handleSearchByNum = (searchedVal: string) => {
    setPage(0);
    setCustomerNum(searchedVal);
  };

  const clearNumSearch = () => {
    if (customerNum !== "") {
      handleSearchByNum("");
    }
  };

  const handleSearch = (searchedVal: string) => {
    setPage(0);
    setSearchBy(searchedVal);
  };

  const clearSearch = () => {
    if (searchBy !== "") {
      handleSearch("");
    }
  };

  //Filters
  const handleChangeServiceFilter = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setSelectedService(value);
  };

  const handleChangePaymentType = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setSelectedPaymentType(value);
  };

  const handleChangeCity = (event: React.SyntheticEvent, newValue: any) => {
    setSelectedCity(newValue);
  };

  const handleChangeLocation = (event: React.SyntheticEvent, newValue: any) => {
    setLocation(newValue);
  };

  const handleExport = () => {
    refetchExportUpcoming().then((response) => {
      if (response.status === "success" && response.data) {
        const utf8CSVData = `\uFEFF${response.data}`;
        downloadFile(utf8CSVData, "text/csv", "upcoming-transactions");
      }
    });
  };

  const handleCloseDrawer = () => {
    setOpen(false);
    setClickedRow(undefined);
  };

  const emptyDataArr = upcomingTransData?.data.length === 0;

  useEffect(() => {
    setPage(0);
    setLastDocumentIds([]);
  }, [
    location,
    startDate,
    endDate,
    selectedService,
    selectedPaymentType,
    searchBy,
  ]);

  useEffect(() => {
    if (upcomingTransData) {
      setTotalCount(upcomingTransData?.totalTransactions);
    }
  }, [upcomingTransData]);

  const sortedLocations = useMemo(() => {
    return (
      locationsData?.locations.sort((a: any, b: any) =>
        a.nameEn?.localeCompare(b.nameEn),
      ) ?? []
    );
  }, [locationsData]);

  const filteredCities = useMemo(() => {
    if (!citiesData?.data) return [];

    if (!location) {
      return citiesData.data;
    }

    const availableLocations = location ? [location] : sortedLocations;

    const availableLocationsCitiesIds = availableLocations.map(
      (loc: any) => loc.cityId,
    );

    return citiesData.data.filter((city) =>
      availableLocationsCitiesIds.includes(city.id),
    );
  }, [location, citiesData, sortedLocations]);

  useEffect(() => {
    setSelectedCity(undefined);
  }, [location]);

  // error handling
  useEffect(() => {
    if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data?.message ?? "Oops! Something went wrong.",
        },
      ]);
    }
  }, [status]);

  return (
    <>
      <Grid container spacing={1} mb={3}>
        {/* search by customer number */}
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Customer Number"
            value={customerNum}
            onChange={(event) => handleSearchByNum(event.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ opacity: !customerNum ? 0 : 1 }}
                >
                  <IconButton size="small" onClick={clearNumSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        {/* locations */}
        <Grid item xs={12} md={2}>
          <CustomAutocomplete
            size="small"
            fullWidth
            disablePortal
            id="combo-box-demo"
            options={sortedLocations}
            getOptionLabel={(option: any) => option.nameEn}
            getOptionKey={(option: any) => option.id}
            value={location}
            onChange={handleChangeLocation}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"Location"} />
            )}
          />
        </Grid>
        {/* city */}
        <Grid item xs={12} md={1.8}>
          <CustomAutocomplete
            key={selectedCity?.id ?? "city-autocomplete"}
            size="small"
            disablePortal
            id="combo-box-demo"
            options={filteredCities}
            getOptionLabel={(option) => option.nameEn}
            getOptionKey={(option) => `${option.id}`}
            value={selectedCity}
            onChange={handleChangeCity}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"City"} />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6} display={"flex"} direction={"row"} gap={1}>
          {/* start date */}
          <DatePicker
            label="Start Date"
            slotProps={{
              textField: {
                size: "small",
                onClick: () => setOpenStartDate(true),
                sx: { width: "126px" },
                inputProps: { readOnly: true },
              },
            }}
            format={"dd/MM/yyyy"}
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            monthsPerRow={4}
            name="start date"
            disableOpenPicker
            open={openStartDate}
            onClose={() => setOpenStartDate(false)}
            minDate={minDate}
            maxDate={maxDate}
          />
          {/* end date */}
          <DatePicker
            label="End Date"
            slotProps={{
              textField: {
                size: "small",
                onClick: () => setOpenEndDate(true),
                sx: { width: "126px" },
                inputProps: { readOnly: true },
              },
            }}
            format={"dd/MM/yyyy"}
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            name="end date"
            disableOpenPicker
            open={openEndDate}
            onClose={() => setOpenEndDate(false)}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Grid>
      </Grid>

      {customerNum || (startDate && endDate && (location || selectedCity)) ? (
        <Paper
          sx={{
            bgcolor: "#1A1A1A",
            boxShadow: "0px 14px 44px 0px rgba(0, 0, 0, 0.35)",
            padding: { xs: "15px", md: "30px" },
          }}
        >
          <Grid container spacing={{ xs: 1.4, sm: 1 }} mb={3}>
            {/* search */}
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search"
                value={searchBy}
                onChange={(event) => handleSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{ opacity: !searchBy ? 0 : 1 }}
                    >
                      <IconButton size="small" onClick={clearSearch}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {/* payment type */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="demo-simple-select-label">
                  Payment Type
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={selectedPaymentType}
                  label="Payment Type"
                  onChange={handleChangePaymentType}
                  fullWidth
                  sx={{ textTransform: "capitalize" }}
                >
                  <MenuItem value={"all"}>All</MenuItem>
                  {paymentsTypes.map((payment) => (
                    <MenuItem
                      key={payment}
                      value={payment}
                      sx={{ textTransform: "capitalize" }}
                    >
                      {payment}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Service */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="demo-simple-select-label">Service</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={selectedService}
                  label="Service"
                  onChange={handleChangeServiceFilter}
                  fullWidth
                >
                  <MenuItem value={"all"}>All</MenuItem>
                  <MenuItem value={"valet"}>Valet</MenuItem>
                  <MenuItem value={"selfParking"}>Self Parking</MenuItem>
                  <MenuItem value={"golfCar"}>Golf Cart</MenuItem>
                  <MenuItem value={"partialCarWash"}>Partial Car Wash</MenuItem>
                  <MenuItem value={"fullCarWash"}>Complete Car Wash</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {!emptyDataArr ? (
              <Grid
                item
                xs={12}
                md={3.2}
                display={"flex"}
                justifyContent={"flex-end"}
              >
                <LoadingButton
                  variant="contained"
                  sx={{ gap: 0.5 }}
                  onClick={handleExport}
                  loading={isFetchingExport}
                >
                  Export
                </LoadingButton>
              </Grid>
            ) : null}
          </Grid>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  {tableHeadText.map((item) => (
                    <TableCell
                      key={item}
                      sx={{
                        fontWeight: 600,
                        color: "#A1A5B7",
                      }}
                    >
                      {item}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableLoadingSkeleton
                  rowsLength={rowsPerPage}
                  cellsLength={8}
                />
              ) : (
                <TableBody>
                  {upcomingTransData?.data?.map((row: any) => {
                    const { paymentStatus } = row;
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => {
                          setOpen(true);
                          setClickedRow(row);
                        }}
                        sx={{
                          cursor: "pointer",
                          "&:last-child td, &:last-child th": {
                            border: 0,
                          },
                        }}
                      >
                        <TableCell>{row.ticketNumber}</TableCell>
                        <TableCell>
                          {row?.checkInTime || row?.arrivalTime
                            ? moment(
                                row?.checkInTime ?? row?.arrivalTime,
                              ).format("DD/MM/yyyy")
                            : ""}
                        </TableCell>
                        <TableCell>{row.locationName}</TableCell>
                        <TableCell>{row.service?.name}</TableCell>
                        <TableCell>
                          {serviceLabels[row.service?.type] ??
                            row.service?.type}
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            color={row.checkOutTime ? "success" : "warning"}
                            label={row.checkOutTime ? "Done" : "Not Done"}
                          />
                        </TableCell>
                        <TableCell>
                          {!!paymentStatus && (
                            <StatusChip
                              color={
                                paymentStatus === "Paid"
                                  ? "success"
                                  : paymentStatus === "Free"
                                    ? "blue"
                                    : paymentStatus === "Refunded"
                                      ? "gray"
                                      : paymentStatus === "Cancelled"
                                        ? "error"
                                        : "warning" //"Pending"
                              }
                              label={paymentStatus}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ textTransform: "capitalize" }}>
                          {row.status}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>

            {emptyDataArr && <NoData />}

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              disabled={isLoading}
            />
          </TableContainer>
          {open && !!clickedRow ? (
            <TransactionsDetailsDrawer
              transDetails={clickedRow}
              open={open}
              onClose={handleCloseDrawer}
            />
          ) : null}
        </Paper>
      ) : (
        <NoFilterSelected />
      )}
    </>
  );
};
export default TransactionsTable;
